import type { Point, Reading } from '../types';
import type { MetricKey } from '../lib/mapMetric';
import { mostRecentValue } from '../lib/latestValue';

/**
 * NDBC serves no CORS headers, so the browser cannot reach it directly. Everything
 * here goes through our own Pages Function, which fetches, parses, and caches.
 * See functions/api/ndbc/[station].ts.
 */
interface ProxyRow {
  t: string;
  /** Null on a wave buoy; see ParsedReading in ../lib/ndbcParse.ts. */
  speedKt: number | null;
  dirDeg: number | null;
  gustKt: number | null;
  waveHeightM: number | null;
  wavePeriodS: number | null;
  waterTempC: number | null;
  airTempC: number | null;
}

/**
 * Rows from the last `history()` call, so the wave and temperature series cost
 * no additional request.
 *
 * Only `history()` writes here. The short latest/batch fetches carry three hours
 * of data, which is not enough to draw a 24-hour panel, and letting them
 * overwrite this would quietly truncate an open popup's charts on the next
 * refresh.
 */
let lastRows: { station: string; rows: ProxyRow[] } | null = null;

/**
 * The wind series, which is only the rows that measured wind.
 *
 * A wave buoy's rows are kept in `lastRows` for the height and temperature
 * panels but cannot appear here: a Reading is a wind observation, and carrying
 * a null speed through would draw a line at zero knots.
 */
function toReadings(rows: ProxyRow[]): Reading[] {
  return rows.flatMap((r) =>
    r.speedKt === null
      ? []
      : [{ t: new Date(r.t), speedKt: r.speedKt, dirDeg: r.dirDeg, gustKt: r.gustKt }],
  );
}

function byTime(rows: ProxyRow[]): ProxyRow[] {
  return rows.slice().sort((a, b) => Date.parse(a.t) - Date.parse(b.t));
}

async function getJson(url: string): Promise<Record<string, unknown>> {
  const res = await fetch(url);
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`NDBC proxy HTTP ${res.status}${detail ? `: ${detail.slice(0, 120)}` : ''}`);
  }
  return res.json();
}

/** 3h is enough to survive a couple of missed 10-minute transmissions. */
const LATEST_HOURS = 3;

/**
 * The current reading for every offshore buoy, in a single request.
 *
 * One call rather than four matters here specifically: these are the only
 * requests the page routes through our own Worker, so they are the only ones
 * that draw down a shared daily quota.
 *
 * Resolves to a map of whatever succeeded. A station missing from it failed,
 * and the caller reports that station as unavailable without disturbing the rest.
 */
export async function latestAll(stationIds: string[]): Promise<Map<string, Reading>> {
  const out = new Map<string, Reading>();
  if (stationIds.length === 0) return out;

  const body = await getJson(
    `/api/ndbc?stations=${encodeURIComponent(stationIds.join(','))}&hours=${LATEST_HOURS}`,
  );
  const stations = body.stations as Record<string, { readings?: ProxyRow[] }> | undefined;
  if (!stations) throw new Error('NDBC proxy returned no stations');

  for (const [id, entry] of Object.entries(stations)) {
    if (!Array.isArray(entry.readings) || entry.readings.length === 0) continue;
    const ordered = byTime(entry.readings);
    recentRows.set(id, ordered);
    const rows = toReadings(ordered);
    // A wave buoy reports no wind, so this can be empty even though the station
    // is reporting; setting rows[-1] would put undefined in a Map<string, Reading>.
    if (rows.length > 0) out.set(id, rows[rows.length - 1]);
  }
  return out;
}

/**
 * The newest row for each station, carrying every sensor rather than just wind.
 *
 * `latestAll` narrows to a wind Reading, which is right for the curated buoys
 * but loses the wave height and temperatures a national marker may be labelled
 * with. This keeps the whole row so the marker can be repainted under any
 * metric filter.
 */
export async function latestRowsFor(
  stationIds: string[],
): Promise<Map<string, {
  t: Date; speedKt: number | null; dirDeg: number | null; gustKt: number | null;
  waveHeightM: number | null; airTempC: number | null; waterTempC: number | null;
}>> {
  const out = new Map<string, {
    t: Date; speedKt: number | null; dirDeg: number | null; gustKt: number | null;
    waveHeightM: number | null; airTempC: number | null; waterTempC: number | null;
  }>();
  if (stationIds.length === 0) return out;

  const body = await getJson(
    `/api/ndbc?stations=${encodeURIComponent(stationIds.join(','))}&hours=${LATEST_HOURS}`,
  );
  const stations = body.stations as Record<string, { readings?: ProxyRow[] }> | undefined;
  if (!stations) throw new Error('NDBC proxy returned no stations');

  for (const [id, entry] of Object.entries(stations)) {
    if (!Array.isArray(entry.readings) || entry.readings.length === 0) continue;
    const ordered = byTime(entry.readings);
    recentRows.set(id, ordered);
    const r = ordered[ordered.length - 1];
    out.set(id, {
      t: new Date(r.t),
      speedKt: r.speedKt,
      dirDeg: r.dirDeg,
      gustKt: r.gustKt,
      waveHeightM: r.waveHeightM,
      airTempC: r.airTempC,
      waterTempC: r.waterTempC,
    });
  }
  return out;
}

/**
 * Rows from the last batch, kept for the map's metric labels.
 *
 * NDBC ships wind, waves and temperatures in one file, so these values arrive
 * with every refresh whether or not anything asks for them. Reading them here
 * costs no request at all — the alternative would be a second fetch for data
 * already in hand.
 *
 * The whole run is kept, not just the newest row: the sensors are not
 * synchronised, so each field has to be followed back to its own last real
 * value. See lib/latestValue.
 */
const recentRows = new Map<string, ProxyRow[]>();

const METRIC_FIELD: Record<MetricKey, (r: ProxyRow) => number | null> = {
  waterTemp: (r) => (r.waterTempC === null ? null : celsiusToF(r.waterTempC)),
  airTemp: (r) => (r.airTempC === null ? null : celsiusToF(r.airTempC)),
  waves: (r) => (r.waveHeightM === null ? null : r.waveHeightM * 3.28084),
  // NDBC buoys are moored in open water and report no tidal datum.
  waterLevel: () => null,
  // Wind comes off the station's own reading, not through this map.
  wind: () => null,
};

export function latestMetric(stationId: string, metric: MetricKey): number | null {
  const rows = recentRows.get(stationId);
  if (!rows || rows.length === 0) return null;
  return mostRecentValue(rows, METRIC_FIELD[metric]);
}

export async function latest(stationId: string): Promise<Reading> {
  const found = await latestAll([stationId]);
  const reading = found.get(stationId);
  if (!reading) throw new Error('No current observation');
  return reading;
}

export async function history(stationId: string, hours = 24): Promise<Reading[]> {
  const body = await getJson(`/api/ndbc/${encodeURIComponent(stationId)}?hours=${hours}`);
  if (!Array.isArray(body.readings)) throw new Error('NDBC proxy returned no readings');

  const rows = byTime(body.readings as ProxyRow[]);
  lastRows = { station: stationId, rows };
  return toReadings(rows);
}

function celsiusToF(c: number): number {
  return c * 1.8 + 32;
}

/**
 * Extra series from the rows the last `history()` call already downloaded.
 * NDBC ships wind, waves, and temperatures in one file, so pulling these out
 * costs nothing extra — but it must run after `history()` for the same station.
 */
function seriesFrom(
  stationId: string,
  pick: (r: ProxyRow) => number | null,
  transform: (v: number) => number = (v) => v,
): Point[] {
  if (!lastRows || lastRows.station !== stationId) return [];
  return lastRows.rows
    .map((r) => ({ raw: pick(r), t: new Date(r.t) }))
    .filter((x): x is { raw: number; t: Date } => x.raw !== null && Number.isFinite(x.raw))
    .map((x) => ({ t: x.t, v: transform(x.raw) }));
}

/** Metres upstream; shown in feet to match the water-level series. */
export const waveHeight = (id: string): Point[] =>
  seriesFrom(id, (r) => r.waveHeightM, (m) => m * 3.28084);

/** Upstream is Celsius; converted to °F to match the CO-OPS english units. */
export const waterTemp = (id: string): Point[] => seriesFrom(id, (r) => r.waterTempC, celsiusToF);
export const airTemp = (id: string): Point[] => seriesFrom(id, (r) => r.airTempC, celsiusToF);
